import { useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { getToken } from "../lib/auth";
import { useAuth } from "../lib/auth";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, BedDouble, Link as LinkIcon, Image, X, Upload } from "lucide-react";
import { toast } from "sonner";
import { formatIDR } from "../lib/format";

const empty = { name: "", base_rate: 0 };

export default function Rooms() {
  const { isOwner } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [deleteId, setDeleteId] = useState(null);
  const [photoRoom, setPhotoRoom] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/rooms`);
      setRooms(res.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (room) => {
    setEditing(room);
    setForm({ name: room.name, base_rate: room.base_rate || 0 });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nama kamar tidak boleh kosong."); return; }
    try {
      const payload = { name: form.name.trim(), base_rate: Number(form.base_rate) || 0 };
      if (editing) {
        await api.put(`/rooms/${editing.id}`, payload);
        toast.success("Kamar diperbarui.");
      } else {
        await api.post(`/rooms`, payload);
        toast.success("Kamar baru ditambahkan.");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan.");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/rooms/${deleteId}`);
      toast.success("Kamar dihapus.");
      setDeleteId(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus.");
      setDeleteId(null);
    }
  };

  const copyIcalUrl = (room) => {
    const url = `${API}/ical/room/${room.id}.ics`;
    navigator.clipboard?.writeText(url);
    toast.success(`iCal URL ${room.name} disalin ke clipboard.`);
  };

  return (
    <>
      <PageHeader
        title="Kelola Kamar"
        subtitle={isOwner
          ? "Atur bungalow: nama, harga dasar, foto, dan iCal feed untuk sync ke Booking.com/Airbnb."
          : "Daftar bungalow di homestay Anda."}
        actions={isOwner ? (
          <Button
            data-testid="btn-add-room"
            onClick={openCreate}
            className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl shadow-md shadow-[#D16B4D]/20"
          >
            <Plus className="h-4 w-4 mr-2" /> Tambah Kamar
          </Button>
        ) : null}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading && <div className="text-[#586D61]">Memuat…</div>}
        {!loading && rooms.map((room) => (
          <RoomCard
            key={room.id}
            room={room}
            isOwner={isOwner}
            onEdit={() => openEdit(room)}
            onDelete={() => setDeleteId(room.id)}
            onCopyIcal={() => copyIcalUrl(room)}
            onManagePhotos={() => setPhotoRoom(room)}
          />
        ))}
      </div>

      {/* Edit/Add Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editing ? "Edit Kamar" : "Tambah Kamar Baru"}
            </DialogTitle>
            <DialogDescription className="text-[#586D61]">
              {editing ? "Update nama dan harga dasar per malam." : "Masukkan nama bungalow dan harga dasar."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 flex flex-col gap-4">
            <div>
              <Label className="text-sm font-medium">Nama Kamar</Label>
              <Input
                data-testid="input-room-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Bungalow Sunset"
                className="mt-1.5 border-[#EBE6DE]"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Harga Dasar per Malam (IDR)</Label>
              <Input
                data-testid="input-room-base-rate"
                type="number"
                min={0}
                value={form.base_rate}
                onChange={(e) => setForm({ ...form, base_rate: e.target.value })}
                placeholder="1000000"
                className="mt-1.5 border-[#EBE6DE] font-mono"
              />
              <div className="text-xs text-[#586D61] mt-1">
                Harga ini otomatis dikalikan dengan multiplier season rule saat membuat booking.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Batal</Button>
            <Button
              data-testid="btn-save-room"
              onClick={save}
              className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl"
            >
              {editing ? "Simpan" : "Tambah"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photos Dialog */}
      <PhotoDialog
        room={photoRoom}
        isOwner={isOwner}
        onClose={() => setPhotoRoom(null)}
        onUpdated={load}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl">Hapus Kamar?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#586D61]">
              Kamar akan dihapus permanen. Tidak dapat dihapus jika masih ada booking.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-confirm-delete-room"
              onClick={doDelete}
              className="bg-[#D14D4D] hover:bg-[#B83E3E] text-white rounded-xl"
            >Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function RoomCard({ room, isOwner, onEdit, onDelete, onCopyIcal, onManagePhotos }) {
  const thumb = room.photos?.[0];
  const token = getToken();
  const thumbSrc = thumb ? `${API}/photos/${room.id}/${thumb.id}?auth=${token}` : null;

  return (
    <div
      data-testid={`room-card-${room.id}`}
      className="kpi-card bg-white border border-[#EBE6DE] rounded-2xl overflow-hidden shadow-sm flex flex-col"
    >
      <div className="h-40 bg-[#FDFBF7] relative overflow-hidden border-b border-[#EBE6DE]">
        {thumbSrc ? (
          <img src={thumbSrc} alt={room.name} className="w-full h-full object-cover" />
        ) : (
          <div className="h-full flex items-center justify-center text-[#586D61]">
            <BedDouble className="h-10 w-10" strokeWidth={1.4} />
          </div>
        )}
        {room.photos?.length > 0 && (
          <div className="absolute top-2 right-2 bg-[#082618]/75 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full backdrop-blur">
            {room.photos.length} foto
          </div>
        )}
      </div>

      <div className="p-5 flex-1 flex flex-col gap-4">
        <div>
          <div className="flex items-center justify-between">
            <div className="font-heading text-lg font-medium text-[#172E21]">{room.name}</div>
            <div className="text-[10px] text-[#586D61] font-mono">#{String(room.order + 1).padStart(2, "0")}</div>
          </div>
          <div className="text-sm text-[#586D61] mt-1">
            Tarif: <span className="font-mono font-medium text-[#172E21]">
              {room.base_rate > 0 ? `${formatIDR(room.base_rate)}/malam` : "Belum diatur"}
            </span>
          </div>
        </div>

        <div className="flex gap-1.5 mt-auto">
          <Button
            data-testid={`btn-photos-${room.id}`}
            variant="ghost" size="sm"
            onClick={onManagePhotos}
            className="flex-1 hover:bg-[#2C7A51]/10 hover:text-[#2C7A51] rounded-lg"
          >
            <Image className="h-4 w-4 mr-1.5" /> Foto
          </Button>
          <Button
            data-testid={`btn-copy-ical-${room.id}`}
            variant="ghost" size="icon"
            onClick={onCopyIcal}
            title="Copy iCal feed URL"
            className="h-9 w-9 hover:bg-[#D16B4D]/10 hover:text-[#D16B4D]"
          >
            <LinkIcon className="h-4 w-4" />
          </Button>
          {isOwner && (
            <>
              <Button
                data-testid={`btn-edit-room-${room.id}`}
                variant="ghost" size="icon"
                onClick={onEdit}
                className="h-9 w-9 hover:bg-[#082618]/10 hover:text-[#082618]"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                data-testid={`btn-delete-room-${room.id}`}
                variant="ghost" size="icon"
                onClick={onDelete}
                className="h-9 w-9 hover:bg-[#D14D4D]/10 hover:text-[#D14D4D]"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PhotoDialog({ room, isOwner, onClose, onUpdated }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const token = getToken();

  if (!room) return null;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if ((room.photos?.length || 0) >= 5) {
      toast.error("Maksimal 5 foto per kamar.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/rooms/${room.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto berhasil diunggah.");
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal upload.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async (photoId) => {
    try {
      await api.delete(`/rooms/${room.id}/photos/${photoId}`);
      toast.success("Foto dihapus.");
      onUpdated();
    } catch {
      toast.error("Gagal hapus foto.");
    }
  };

  return (
    <Dialog open={!!room} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Foto {room.name}</DialogTitle>
          <DialogDescription className="text-[#586D61]">
            Maksimal 5 foto per kamar. Format: JPG/PNG/WEBP. Ukuran max 5MB.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 py-2">
          {(room.photos || []).map((p) => (
            <div
              key={p.id}
              data-testid={`photo-${p.id}`}
              className="relative aspect-square rounded-xl overflow-hidden border border-[#EBE6DE] group"
            >
              <img
                src={`${API}/photos/${room.id}/${p.id}?auth=${token}`}
                alt={p.original_filename}
                className="w-full h-full object-cover"
              />
              {isOwner && (
                <button
                  data-testid={`btn-delete-photo-${p.id}`}
                  onClick={() => removePhoto(p.id)}
                  className="absolute top-2 right-2 h-8 w-8 rounded-full bg-[#D14D4D] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {isOwner && (room.photos?.length || 0) < 5 && (
            <label
              data-testid="upload-photo-slot"
              className="aspect-square rounded-xl border-2 border-dashed border-[#EBE6DE] hover:border-[#D16B4D] hover:text-[#D16B4D] flex flex-col items-center justify-center cursor-pointer text-[#586D61] transition-colors gap-2"
            >
              <Upload className="h-6 w-6" strokeWidth={1.6} />
              <span className="text-xs font-medium">{uploading ? "Uploading…" : "Upload"}</span>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleUpload}
                data-testid="input-upload-photo"
              />
            </label>
          )}
        </div>

        {(room.photos?.length || 0) === 0 && !isOwner && (
          <div className="text-center text-[#586D61] py-8">Belum ada foto.</div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Tutup</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
